'use client';

import React, { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { PageType, useGTM } from '@/features/Analytics';
import { usePathname, useRouter } from '@/navigation';
import { useHttp } from '@/shared/api/provider';
import { classNames } from '@/shared/lib/classnames/classnames';
import { Button } from '@/shared/ui/button';
import { ReviewItem } from './ReviewItem';
import { ReviewsEmpty } from './ReviewsEmpty';
import { ReviewsHeader } from './ReviewsHeader';
import { TabList } from './TabList';
import type { IPagination } from '@/entities/common/types/api';
import { ReviewsMood } from '@/entities/review/model/types';
import type { IReview, IReviewStats } from '@/entities/review/model/types';
import type { ReviewType } from '@/app/[locale]/[city]/doctor/[alias]/model/types';
interface Props {
  initialReviews: IReview[];
  initialPagination: IPagination;
  id: number;
  stats: IReviewStats;
  avatar: string;
  type: ReviewType;
  alias: string;
}
export const ReviewsListClient: React.FC<Props> = ({
  initialReviews,
  initialPagination,
  id,
  stats,
  avatar,
  type,
  alias
}) => {
  const repositories = useHttp();
  const t = useTranslations('doctor.reviews');
  const {
    trackEvent
  } = useGTM(PageType.DOCTOR_PROFILE || PageType.MEDCENTER_PROFILE);
  const pathname = usePathname();
  const router = useRouter();
  const [filter, setFilter] = useState<ReviewsMood>(ReviewsMood.Any);
  const [currentReviews, setCurrentReviews] = useState<IReview[]>(initialReviews);
  const [pagination, setPagination] = useState<IPagination>(initialPagination);
  const [loading, setLoading] = useState(false);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const fetchReviews = async (mood: ReviewsMood, page: number = 1) => {
    setLoading(true);
    const {
      review: newReviews,
      pagination: newPagination
    } = await repositories.doctor.getReviewsDoctor({
      type: type,
      id: id,
      mood: mood,
      page: page
    });
    setLoading(false);
    return {
      newReviews,
      newPagination
    };
  };
  const loadMoreReviews = async () => {
    if (!pagination.hasNextPage || loading) return;
    const nextPage = pagination.currentPage + 1;
    const {
      newReviews,
      newPagination
    } = await fetchReviews(filter, nextPage);
    setCurrentReviews([...currentReviews, ...newReviews]);
    setPagination(newPagination);
  };
  const goFeedback = () => {
    setFeedbackLoading(true);
    trackEvent('click_write_doctor_review');
    router.push(`${pathname}/feedback`);
  };
  useEffect(() => {
    const loadReviews = async () => {
      const {
        newReviews,
        newPagination
      } = await fetchReviews(filter);
      setCurrentReviews(newReviews);
      setPagination(newPagination);
    };
    loadReviews();
  }, [filter, id]);
  return <div className="rounded-2xl bg-bgPrimary p-4" data-sentry-component="ReviewsListClient" data-sentry-source-file="ReviewsListClient.tsx">
      <ReviewsHeader goFeedback={goFeedback} feedbackLoading={feedbackLoading} data-sentry-element="ReviewsHeader" data-sentry-source-file="ReviewsListClient.tsx" />

      {initialReviews.length > 0 ? <>
          <TabList setFilter={setFilter} filter={filter} stats={stats} />

          <div>
            {currentReviews.map((review, index) => <ReviewItem key={index} {...review} avatar={avatar} alias={alias} />)}
          </div>

          {pagination.hasNextPage && <Button variant={'secondary-main'} onClick={loadMoreReviews} textWeight={'bold'} className={classNames('mt-4 w-full py-3 text-center')} loading={loading} disabled={loading}>
              {t('show_more')}
            </Button>}
        </> : <ReviewsEmpty />}
    </div>;
};